import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Card,
  SectionHeader,
  Input,
  Button,
  Icon,
  CustomSelect,
  Modal,
} from "../components/ui";
import { DropzoneField } from "../components/DropzoneField";
import { useYstReducedMotion } from "../../../components/motion/ystMotion";
import { useStaffSettingsState } from "../hooks/useStaffSettingsState";
import { useModuleEnablementState } from "../hooks/useModuleEnablementState";
import { useListModuleEnablementAuditEventsQuery } from "../../../state/domainRouterApi";
import { useShellProfile } from "../../../state/useShellProfile";
import { resolvePrimaryAddressLabel } from "../utils/onboardingSettings";
import type {
  GovernanceScope,
  MaterializedModuleGovernanceModule,
} from "../../../platform-governance/moduleGovernanceCatalog";

type AddressRecord = {
  id: string;
  label: string;
  street: string;
  city: string;
  state: string;
  zip: string;
};

type DivisionRow = {
  id: string;
  name: string;
  address: string;
  gender: "Boys" | "Girls" | "Preschool";
  cap: number;
  enabled: boolean;
};

type ResourceRow = {
  id: string;
  name: string;
  meta: string;
  icon: string;
  color: string;
  bg: string;
  type: "handbook" | "calendar";
  date: string;
};

type OrganizationDraft = {
  name: string;
  shortName: string;
  emailAdmissions: string;
  website: string;
  phone: string;
  timezone: string;
  brandColor: string;
};

type ParnesHayomPricingDraft = {
  oneClassAmount: string;
  oneDivisionAmount: string;
  schoolwideAmount: string;
};

const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const RESOURCE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_RESOURCES = 10;
const DEFAULT_PARNES_HAYOM_PRICING = {
  oneClassAmount: 180,
  oneDivisionAmount: 500,
  schoolwideAmount: 1000,
} as const;

const USA_TIMEZONE_OPTIONS = [
  { label: "Eastern - America/New_York", value: "America/New_York" },
  { label: "Central - America/Chicago", value: "America/Chicago" },
  { label: "Mountain - America/Denver", value: "America/Denver" },
  { label: "Mountain no DST - America/Phoenix", value: "America/Phoenix" },
  { label: "Pacific - America/Los_Angeles", value: "America/Los_Angeles" },
  { label: "Alaska - America/Anchorage", value: "America/Anchorage" },
  { label: "Aleutian - America/Adak", value: "America/Adak" },
  { label: "Hawaii - Pacific/Honolulu", value: "Pacific/Honolulu" },
  { label: "Atlantic - America/Puerto_Rico", value: "America/Puerto_Rico" },
  { label: "Samoa - Pacific/Pago_Pago", value: "Pacific/Pago_Pago" },
  { label: "Chamorro - Pacific/Guam", value: "Pacific/Guam" },
] as const;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const toText = (value: unknown): string => String(value ?? "").trim();

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeParnesHayomPricingDraft = (
  value: unknown,
): ParnesHayomPricingDraft => {
  const record = asRecord(value);
  return {
    oneClassAmount: String(
      toFiniteNumber(
        record.oneClassAmount,
        DEFAULT_PARNES_HAYOM_PRICING.oneClassAmount,
      ),
    ),
    oneDivisionAmount: String(
      toFiniteNumber(
        record.oneDivisionAmount,
        DEFAULT_PARNES_HAYOM_PRICING.oneDivisionAmount,
      ),
    ),
    schoolwideAmount: String(
      toFiniteNumber(
        record.schoolwideAmount,
        DEFAULT_PARNES_HAYOM_PRICING.schoolwideAmount,
      ),
    ),
  };
};

const parseWholeDollarAmount = (value: string, fallback: number): number => {
  const normalized = value.replace(/[^\d.-]/g, "").trim();
  const parsed = Number.parseInt(normalized, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "off"].includes(normalized)) {
      return false;
    }
  }
  return undefined;
};

const resolveParnesHayomVisibility = (
  organizationExt: Record<string, unknown>,
): boolean => {
  const parnesPayload = asRecord(organizationExt.parnesHayom);
  const parnasPayload = asRecord(organizationExt.parnasHayom);
  const candidates = [
    organizationExt.parnesHayomVisibleToParents,
    organizationExt.parnasHayomVisibleToParents,
    organizationExt.parnes_hayom_visible_to_parents,
    organizationExt.parnas_hayom_visible_to_parents,
    parnesPayload.visibleToParents,
    parnesPayload.visible_to_parents,
    parnasPayload.visibleToParents,
    parnasPayload.visible_to_parents,
  ];
  for (const candidate of candidates) {
    const parsed = parseBoolean(candidate);
    if (typeof parsed === "boolean") {
      return parsed;
    }
  }
  return true;
};

const getAddressFromRecord = (
  record: Record<string, unknown>,
  id: string,
  label: string,
): AddressRecord => ({
  id,
  label,
  street: toText(record.street),
  city: toText(record.city),
  state: toText(record.state),
  zip: toText(record.zip),
});

const buildAddressList = (
  organization: Record<string, unknown>,
): AddressRecord[] => {
  const mainAddress = getAddressFromRecord(
    asRecord(organization.address),
    "main-campus",
    resolvePrimaryAddressLabel(organization),
  );
  const ext = asRecord(organization.ext);
  const additionalRaw = Array.isArray(ext.additionalAddresses)
    ? ext.additionalAddresses
    : [];
  const additional = additionalRaw.map((entry, index) => {
    const record = asRecord(entry);
    const label = toText(record.label) || `Campus ${index + 2}`;
    return getAddressFromRecord(
      record,
      toText(record.id) || `additional-${index + 1}`,
      label,
    );
  });
  return [mainAddress, ...additional];
};

const normalizeDivisions = (
  divisions: unknown,
  fallbackAddressLabel: string,
): DivisionRow[] => {
  if (!Array.isArray(divisions)) {
    return [];
  }

  return divisions.map((entry, index) => {
    const record = asRecord(entry);
    const ext = asRecord(record.ext);
    const type = toText(record.type);
    const gender: DivisionRow["gender"] =
      type === "girls" ? "Girls" : type === "preschool" ? "Preschool" : "Boys";
    return {
      id: toText(record.id) || `division-${index + 1}`,
      name: toText(record.displayName) || `${gender} Division`,
      address: toText(ext.addressLabel) || fallbackAddressLabel,
      gender,
      cap: toFiniteNumber(ext.capacity, 0),
      enabled: record.enabled !== false,
    };
  });
};

const resourceVisuals = (type: "handbook" | "calendar") =>
  type === "calendar"
    ? { icon: "Calendar", color: "text-jungle", bg: "bg-success-background" }
    : { icon: "FileText", color: "text-strawberry", bg: "bg-error-background" };

const normalizeResources = (resources: unknown): ResourceRow[] => {
  if (!Array.isArray(resources)) {
    return [];
  }

  return resources.map((entry, index) => {
    const record = asRecord(entry);
    const type = toText(record.type) === "calendar" ? "calendar" : "handbook";
    const date = toText(record.date);
    const visuals = resourceVisuals(type);
    return {
      id: toText(record.id) || `resource-${index + 1}`,
      name: toText(record.title) || "Untitled Resource",
      meta: `${type.toUpperCase()}${date ? ` • Updated ${date}` : ""}`,
      icon: visuals.icon,
      color: visuals.color,
      bg: visuals.bg,
      type,
      date,
    };
  });
};

const buildResourceMeta = (
  type: "handbook" | "calendar",
  date: string,
): string => `${type.toUpperCase()}${date ? ` • Updated ${date}` : ""}`;

const isSchoolModuleEnabled = (
  schoolModuleId: string,
  modules: { moduleId: string; enabled: boolean; alwaysOn?: boolean }[],
): boolean => {
  const module = modules.find((entry) => entry.moduleId === schoolModuleId);
  return !module || module.enabled || Boolean(module.alwaysOn);
};

const dependencyModeLabel: Record<
  "school-module" | "shared-data-source" | "none",
  string
> = {
  "school-module": "Depends on school module",
  "shared-data-source": "Shared data source",
  none: "Independent",
};

const buildModuleToggleAriaLabel = (
  scope: GovernanceScope,
  module: Pick<MaterializedModuleGovernanceModule, "label">,
): string =>
  `${scope === "parent" ? "Parent module" : "School module"} ${module.label}`;

const buildRouteGroupToggleAriaLabel = (
  module: Pick<MaterializedModuleGovernanceModule, "label">,
  routeGroupLabel: string,
): string => `${module.label} route group ${routeGroupLabel}`;

const buildScopedModuleStateKey = (
  scope: GovernanceScope,
  moduleId: string,
): string => `${scope}:${moduleId}`;

const buildDisabledModuleMessage = (
  module: Pick<MaterializedModuleGovernanceModule, "disableBehavior">,
): string =>
  module.disableBehavior === "hide-sidebar-only"
    ? "Hidden from the shell temp section. Direct preview routes stay mounted for QA and review."
    : "Hidden from navigation. Direct URLs are denied.";

const buildDisableModuleConfirmation = (
  module: Pick<MaterializedModuleGovernanceModule, "disableBehavior">,
): string =>
  module.disableBehavior === "hide-sidebar-only"
    ? "The shell temp section will disappear for school and parent personas. Direct preview routes remain mounted for QA and design review."
    : "Staff will lose access immediately. This module will be hidden and all direct URLs denied.";

export default function General() {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [logoSizeError, setLogoSizeError] = useState<string | null>(null);
  const [resourceGuardError, setResourceGuardError] = useState<string | null>(
    null,
  );
  const logoInputRef = useRef<HTMLInputElement>(null);
  const resourceReplaceRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set(),
  );
  const [pendingDisableId, setPendingDisableId] = useState<string | null>(null);
  const [lastToggledModuleId, setLastToggledModuleId] = useState<string | null>(
    null,
  );
  const { settingsState, persistPatch } = useStaffSettingsState();
  const { effectivePersona } = useShellProfile();
  const isSuperAdmin = effectivePersona === "super-administrator";

  const reducedMotion = useYstReducedMotion();
  const moduleMotionProps = {
    initial: { opacity: 0, y: reducedMotion ? 0 : 6 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: reducedMotion ? 0 : 0.18,
      ease: [0.16, 1, 0.3, 1] as number[],
    },
  };
  const {
    moduleEnablementState,
    persistScopePatch,
    isLoading: isLoadingModules,
    isSaving: isSavingModules,
  } = useModuleEnablementState();
  const { data: moduleAuditData, isLoading: isLoadingModuleAudit } =
    useListModuleEnablementAuditEventsQuery({ limit: 5 });

  const [organization, setOrganization] = useState<OrganizationDraft>({
    name: "",
    shortName: "",
    emailAdmissions: "",
    website: "",
    phone: "",
    timezone: "",
    brandColor: "#2697a2",
  });
  const [addresses, setAddresses] = useState<AddressRecord[]>([]);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [divisions, setDivisions] = useState<DivisionRow[]>([]);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [parnesHayomPricing, setParnesHayomPricing] =
    useState<ParnesHayomPricingDraft>(() =>
      normalizeParnesHayomPricingDraft(undefined),
    );
  const [isParnesHayomVisibleToParents, setIsParnesHayomVisibleToParents] =
    useState(true);

  const organizationPayload = settingsState.organization;
  const divisionsPayload = settingsState.divisions;
  const parentResourcesPayload = settingsState.parentResources;

  useEffect(() => {
    const orgExt = asRecord(organizationPayload.ext);
    setOrganization({
      name: organizationPayload.name,
      shortName: organizationPayload.shortName,
      emailAdmissions: organizationPayload.emailAdmissions,
      website: organizationPayload.website,
      phone: toText(orgExt.phone),
      timezone: toText(orgExt.timezone),
      brandColor: toText(orgExt.brandColor) || "#2697a2",
    });
  }, [organizationPayload]);

  useEffect(() => {
    const nextAddresses = buildAddressList(asRecord(organizationPayload));
    setAddresses(nextAddresses);
    const fallbackAddress = nextAddresses[0]?.label || "Main Campus";
    setDivisions(normalizeDivisions(divisionsPayload, fallbackAddress));
  }, [organizationPayload, divisionsPayload]);

  useEffect(() => {
    setResources(normalizeResources(parentResourcesPayload));
  }, [parentResourcesPayload]);

  useEffect(() => {
    const organizationExt = asRecord(organizationPayload.ext);
    setParnesHayomPricing(
      normalizeParnesHayomPricingDraft(organizationExt.parnesHayomPricing),
    );
    setIsParnesHayomVisibleToParents(
      resolveParnesHayomVisibility(organizationExt),
    );
  }, [organizationPayload]);

  const addressOptions = useMemo(
    () =>
      addresses.map((address) => ({
        label: address.label,
        value: address.label,
      })),
    [addresses],
  );

  const persistOrganization = async (nextOrganization: OrganizationDraft) => {
    await persistPatch((current) => {
      const ext = asRecord(current.organization.ext);
      return {
        ...current,
        organization: {
          ...current.organization,
          name: nextOrganization.name,
          shortName: nextOrganization.shortName,
          emailAdmissions: nextOrganization.emailAdmissions,
          website: nextOrganization.website,
          ext: {
            ...ext,
            phone: nextOrganization.phone,
            timezone: nextOrganization.timezone,
            brandColor: nextOrganization.brandColor,
          },
        },
      };
    });
  };

  const persistAddresses = async (nextAddresses: AddressRecord[]) => {
    if (!nextAddresses.length) return;
    await persistPatch((current) => {
      const [primary, ...additional] = nextAddresses;
      const ext = asRecord(current.organization.ext);
      current.organization = {
        ...current.organization,
        address: {
          street: primary.street,
          city: primary.city,
          state: primary.state,
          zip: primary.zip,
        },
        ext: {
          ...ext,
          additionalAddresses: additional.map((address) => ({
            id: address.id,
            label: address.label,
            street: address.street,
            city: address.city,
            state: address.state,
            zip: address.zip,
          })),
        },
      };
      return current;
    });
  };

  const persistDivisions = async (nextDivisions: DivisionRow[]) => {
    await persistPatch((current) => {
      current.divisions = nextDivisions.map((division) => {
        const existing = current.divisions.find(
          (entry) => entry.id === division.id,
        );
        const existingExt = asRecord(existing?.ext);
        return {
          id: division.id,
          enabled: division.enabled,
          type:
            division.gender === "Girls"
              ? "girls"
              : division.gender === "Preschool"
                ? "preschool"
                : "boys",
          displayName: division.name,
          secretaryEmail: existing?.secretaryEmail,
          principalId: existing?.principalId,
          primaryStaffId: existing?.primaryStaffId,
          ext: {
            ...existingExt,
            addressLabel: division.address,
            capacity: division.cap,
          },
        };
      });
      return current;
    });
  };

  const persistResources = async (nextResources: ResourceRow[]) => {
    await persistPatch((current) => {
      current.parentResources = nextResources.map((resource) => ({
        id: resource.id,
        title: resource.name,
        type: resource.type,
        date: resource.date,
        meta: resource.meta,
      }));
      return current;
    });
  };

  const persistParnesHayomPricing = async (
    nextPricing: ParnesHayomPricingDraft,
  ) => {
    await persistPatch((current) => {
      const organizationExt = asRecord(current.organization.ext);
      const existingPricing = asRecord(organizationExt.parnesHayomPricing);
      current.organization = {
        ...current.organization,
        ext: {
          ...organizationExt,
          parnesHayomPricing: {
            oneClassAmount: parseWholeDollarAmount(
              nextPricing.oneClassAmount,
              toFiniteNumber(
                existingPricing.oneClassAmount,
                DEFAULT_PARNES_HAYOM_PRICING.oneClassAmount,
              ),
            ),
            oneDivisionAmount: parseWholeDollarAmount(
              nextPricing.oneDivisionAmount,
              toFiniteNumber(
                existingPricing.oneDivisionAmount,
                DEFAULT_PARNES_HAYOM_PRICING.oneDivisionAmount,
              ),
            ),
            schoolwideAmount: parseWholeDollarAmount(
              nextPricing.schoolwideAmount,
              toFiniteNumber(
                existingPricing.schoolwideAmount,
                DEFAULT_PARNES_HAYOM_PRICING.schoolwideAmount,
              ),
            ),
          },
        },
      };
      return current;
    });
  };

  const persistParnesHayomVisibility = async (isVisible: boolean) => {
    await persistPatch((current) => {
      const organizationExt = asRecord(current.organization.ext);
      const existingParnesPayload = asRecord(organizationExt.parnesHayom);
      const existingParnasPayload = asRecord(organizationExt.parnasHayom);
      current.organization = {
        ...current.organization,
        ext: {
          ...organizationExt,
          parnesHayomVisibleToParents: isVisible,
          parnasHayomVisibleToParents: isVisible,
          parnes_hayom_visible_to_parents: isVisible,
          parnas_hayom_visible_to_parents: isVisible,
          parnesHayom: {
            ...existingParnesPayload,
            visibleToParents: isVisible,
            visible_to_parents: isVisible,
          },
          parnasHayom: {
            ...existingParnasPayload,
            visibleToParents: isVisible,
            visible_to_parents: isVisible,
          },
        },
      };
      return current;
    });
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > LOGO_MAX_BYTES) {
      setLogoSizeError(
        `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 2 MB.`,
      );
      event.target.value = "";
      return;
    }
    setLogoSizeError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result;
      if (typeof url !== "string" || !url.trim()) {
        return;
      }
      setProfileImage(url);
      void persistPatch((current) => ({
        ...current,
        organization: {
          ...current.organization,
          logo: url,
        },
      }));
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  useEffect(() => {
    setProfileImage(organizationPayload.logo);
  }, [organizationPayload.logo]);

  const updateOrganizationField = (
    field: keyof OrganizationDraft,
    value: string,
  ) => {
    setOrganization((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleOrganizationBlur = () => {
    void persistOrganization(organization);
  };

  const updateParnesHayomPricingField = (
    field: keyof ParnesHayomPricingDraft,
    value: string,
  ) => {
    setParnesHayomPricing((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleParnesHayomPricingBlur = () => {
    void persistParnesHayomPricing(parnesHayomPricing);
  };

  const handleParnesHayomVisibilityToggle = () => {
    const nextVisibility = !isParnesHayomVisibleToParents;
    setIsParnesHayomVisibleToParents(nextVisibility);
    void persistParnesHayomVisibility(nextVisibility);
  };

  const addAddress = () => {
    const label = toText(newAddress.label) || `Campus ${addresses.length + 1}`;
    const nextAddresses = [
      ...addresses,
      {
        id: `address-${Date.now()}`,
        label,
        street: toText(newAddress.street),
        city: toText(newAddress.city),
        state: toText(newAddress.state),
        zip: toText(newAddress.zip),
      },
    ];
    setAddresses(nextAddresses);
    setIsAddressModalOpen(false);
    setNewAddress({ label: "", street: "", city: "", state: "", zip: "" });
    void persistAddresses(nextAddresses);
  };

  const removeAddress = (addressId: string) => {
    const nextAddresses = addresses.filter(
      (address) => address.id !== addressId,
    );
    if (!nextAddresses.length) return;
    setAddresses(nextAddresses);
    void persistAddresses(nextAddresses);
  };

  const addDivision = () => {
    const nextDivision: DivisionRow = {
      id: `division-${Date.now()}`,
      name: "New Division",
      address: addresses[0]?.label || "Main Campus",
      gender: "Boys",
      cap: 0,
      enabled: true,
    };
    const nextDivisions = [...divisions, nextDivision];
    setDivisions(nextDivisions);
    void persistDivisions(nextDivisions);
  };

  const updateDivision = (id: string, patch: Partial<DivisionRow>) => {
    setDivisions((previous) =>
      previous.map((division) =>
        division.id === id ? { ...division, ...patch } : division,
      ),
    );
  };

  const commitDivisions = () => {
    void persistDivisions(divisions);
  };

  const handleAddResourceFile = (file: File) => {
    if (resources.length >= MAX_RESOURCES) {
      setResourceGuardError(
        `Maximum ${MAX_RESOURCES} resources allowed. Remove one before adding another.`,
      );
      return;
    }
    setResourceGuardError(null);
    const date = new Date().toISOString().slice(0, 10);
    const type: ResourceRow["type"] = file.name
      .toLowerCase()
      .includes("calendar")
      ? "calendar"
      : "handbook";
    const visuals = resourceVisuals(type);
    const nextResources = [
      ...resources,
      {
        id: `resource-${Date.now()}`,
        name: file.name,
        meta: buildResourceMeta(type, date),
        icon: visuals.icon,
        color: visuals.color,
        bg: visuals.bg,
        type,
        date,
      },
    ];
    setResources(nextResources);
    void persistResources(nextResources);
  };

  const replaceResource = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > RESOURCE_MAX_BYTES) {
      setResourceGuardError(
        `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 10 MB.`,
      );
      event.target.value = "";
      return;
    }
    setResourceGuardError(null);
    const date = new Date().toISOString().slice(0, 10);
    const nextResources = resources.map((resource) =>
      resource.id === id
        ? {
            ...resource,
            name: file.name,
            date,
            meta: buildResourceMeta(resource.type, date),
          }
        : resource,
    );
    setResources(nextResources);
    void persistResources(nextResources);
    event.target.value = "";
  };

  const canDisableDivision = (id: string): boolean =>
    divisions.filter((d) => d.enabled && d.id !== id).length > 0;

  const deleteResource = (id: string) => {
    const nextResources = resources.filter((resource) => resource.id !== id);
    setResources(nextResources);
    void persistResources(nextResources);
  };

  const schoolModuleLabelById = useMemo(
    () =>
      new Map(
        moduleEnablementState.schoolModules.map(
          (module) => [module.moduleId, module.label] as const,
        ),
      ),
    [moduleEnablementState.schoolModules],
  );
  const visibleSchoolModules = useMemo(
    () =>
      moduleEnablementState.schoolModules.filter(
        (module) => module.showInEnablementUi !== false,
      ),
    [moduleEnablementState.schoolModules],
  );
  const visibleParentModules = useMemo(
    () =>
      moduleEnablementState.parentModules.filter(
        (module) => module.showInEnablementUi !== false,
      ),
    [moduleEnablementState.parentModules],
  );

  const toggleModule = async (
    scope: GovernanceScope,
    moduleId: string,
    enabled: boolean,
  ) => {
    await persistScopePatch(scope, (current) =>
      current.map((module) =>
        module.moduleId === moduleId ? { ...module, enabled } : module,
      ),
    );
  };

  const toggleRouteGroup = async (
    scope: GovernanceScope,
    moduleId: string,
    routeGroupId: string,
    enabled: boolean,
  ) => {
    await persistScopePatch(scope, (current) =>
      current.map((module) =>
        module.moduleId === moduleId
          ? {
              ...module,
              routeGroups: module.routeGroups.map((routeGroup) =>
                routeGroup.routeGroupId === routeGroupId
                  ? { ...routeGroup, enabled }
                  : routeGroup,
              ),
            }
          : module,
      ),
    );
  };

  /** Intercept school module disable — gate with confirmation row. Re-enables fire immediately. */
  const handleSchoolModuleToggleIntent = (
    moduleId: string,
    currentlyEnabled: boolean,
  ) => {
    const moduleStateKey = buildScopedModuleStateKey("school", moduleId);
    if (currentlyEnabled) {
      setPendingDisableId(moduleStateKey);
    } else {
      void toggleModule("school", moduleId, true);
      setLastToggledModuleId(moduleStateKey);
      setExpandedModules((prev) => new Set([...prev, moduleStateKey]));
    }
  };

  const confirmDisableModule = async (moduleId: string) => {
    await toggleModule("school", moduleId, false);
    setPendingDisableId(null);
    setLastToggledModuleId(buildScopedModuleStateKey("school", moduleId));
  };

  const toggleModuleExpanded = (scope: GovernanceScope, moduleId: string) => {
    const moduleStateKey = buildScopedModuleStateKey(scope, moduleId);
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleStateKey)) {
        next.delete(moduleStateKey);
      } else {
        next.add(moduleStateKey);
      }
      return next;
    });
  };

  // Auto-clear lastToggledModuleId after 30s
  useEffect(() => {
    if (!lastToggledModuleId) return;
    const timer = window.setTimeout(() => setLastToggledModuleId(null), 30_000);
    return () => window.clearTimeout(timer);
  }, [lastToggledModuleId]);

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <Card>
        <SectionHeader
          title="Organization Profile"
          description="Manage your school's identity and branding."
        />

        <div className="flex flex-col md:flex-row gap-8 mb-6 border-b border-border">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-5">
              <input
                type="file"
                ref={logoInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleLogoUpload}
                data-testid="settings-general-logo-input"
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => logoInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    logoInputRef.current?.click();
                  }
                }}
                className="h-24 w-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted text-text-tertiary overflow-hidden relative"
                style={
                  profileImage
                    ? {
                        backgroundImage: `url(${profileImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : {}
                }
                data-testid="settings-general-logo-preview"
              >
                {!profileImage && <Icon name="Upload" className="w-8 h-8" />}
              </div>
              <div>
                <p className="font-semibold text-sm text-irongrey">
                  School Logo
                </p>
                <p className="text-xs text-text-secondary mb-3 mt-1">
                  PNG or SVG, max 2 MB. Displayed in navigation and emails.
                </p>
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 border border-border shadow-sm text-xs font-medium rounded text-irongrey bg-white hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  data-testid="settings-general-logo-upload-button"
                >
                  Upload New
                </button>
                {logoSizeError && (
                  <p
                    className="mt-2 flex items-center gap-1.5 text-xs text-strawberry"
                    role="alert"
                  >
                    <Icon name="AlertCircle" className="w-3.5 h-3.5 shrink-0" />
                    {logoSizeError}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="School Name"
            value={organization.name}
            onChange={(event) =>
              updateOrganizationField("name", event.target.value)
            }
            onBlur={handleOrganizationBlur}
          />
          <Input
            label="Short Name (Code)"
            value={organization.shortName}
            onChange={(event) =>
              updateOrganizationField("shortName", event.target.value)
            }
            onBlur={handleOrganizationBlur}
            subLabel="Used for generating reference codes (e.g. YSV-2024-001)"
          />
          <Input
            label="Admissions Email"
            type="email"
            value={organization.emailAdmissions}
            onChange={(event) =>
              updateOrganizationField("emailAdmissions", event.target.value)
            }
            onBlur={handleOrganizationBlur}
          />
          <Input
            label="Website URL"
            type="url"
            value={organization.website}
            onChange={(event) =>
              updateOrganizationField("website", event.target.value)
            }
            onBlur={handleOrganizationBlur}
          />
          <Input
            label="Phone Number"
            type="tel"
            value={organization.phone}
            onChange={(event) =>
              updateOrganizationField("phone", event.target.value)
            }
            onBlur={handleOrganizationBlur}
          />
          <div>
            <label className="block text-sm font-medium text-irongrey mb-1">
              Timezone
            </label>
            <select
              value={organization.timezone}
              onChange={(event) =>
                updateOrganizationField("timezone", event.target.value)
              }
              onBlur={handleOrganizationBlur}
              className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-10 px-3 transition-colors border bg-white"
            >
              {USA_TIMEZONE_OPTIONS.map((timezone) => (
                <option key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHeader
          title="Addresses"
          description="Manage physical locations for your organization."
          action={
            <Button
              data-testid="settings-general-add-address-button"
              variant="outline"
              onClick={() => setIsAddressModalOpen(true)}
            >
              Add Address
            </Button>
          }
        />
        <div
          data-testid="settings-general-addresses-mobile-list"
          className="space-y-3 md:hidden"
        >
          {addresses.map((address) => (
            <div
              key={address.id}
              data-testid={`settings-general-address-mobile-card-${address.id}`}
              className="rounded-md border border-border bg-white p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Label
                  </p>
                  <p className="mt-1 text-sm font-medium text-irongrey">
                    {address.label}
                  </p>
                </div>
                <button
                  onClick={() => removeAddress(address.id)}
                  className="shrink-0 text-text-tertiary transition-colors hover:text-strawberry"
                  aria-label={`Remove ${address.label}`}
                >
                  <Icon name="Trash2" className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 space-y-3 text-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Street
                  </p>
                  <p className="mt-1 break-words text-text-secondary">
                    {address.street || "—"}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      City
                    </p>
                    <p className="mt-1 break-words text-text-secondary">
                      {address.city || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      State
                    </p>
                    <p className="mt-1 text-text-secondary">
                      {address.state || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
                      ZIP
                    </p>
                    <p className="mt-1 text-text-secondary">
                      {address.zip || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden overflow-hidden rounded-md border border-border bg-muted md:block">
          <div className="grid grid-cols-12 gap-4 border-b border-border bg-muted px-4 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
            <div className="col-span-3">Label</div>
            <div className="col-span-4">Street</div>
            <div className="col-span-2">City</div>
            <div className="col-span-1">State</div>
            <div className="col-span-2">ZIP</div>
          </div>
          {addresses.map((address) => (
            <div
              key={address.id}
              className="grid grid-cols-12 gap-4 items-center border-b border-border bg-white px-4 py-3 text-sm last:border-b-0"
            >
              <div className="col-span-3 font-medium text-irongrey">
                {address.label}
              </div>
              <div className="col-span-4 text-text-secondary">
                {address.street}
              </div>
              <div className="col-span-2 text-text-secondary">
                {address.city}
              </div>
              <div className="col-span-1 text-text-secondary">
                {address.state}
              </div>
              <div className="col-span-2 flex items-center justify-between text-text-secondary">
                <span>{address.zip}</span>
                <button
                  onClick={() => removeAddress(address.id)}
                  className="text-text-tertiary transition-colors hover:text-strawberry"
                >
                  <Icon name="Trash2" className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Modal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        title="Add New Address"
      >
        <div className="space-y-4">
          <Input
            label="Label"
            value={newAddress.label}
            onChange={(event) =>
              setNewAddress({ ...newAddress, label: event.target.value })
            }
            placeholder="e.g. Main Campus"
          />
          <Input
            label="Street"
            value={newAddress.street}
            onChange={(event) =>
              setNewAddress({ ...newAddress, street: event.target.value })
            }
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              value={newAddress.city}
              onChange={(event) =>
                setNewAddress({ ...newAddress, city: event.target.value })
              }
            />
            <Input
              label="State"
              value={newAddress.state}
              onChange={(event) =>
                setNewAddress({ ...newAddress, state: event.target.value })
              }
            />
          </div>
          <Input
            label="ZIP"
            value={newAddress.zip}
            onChange={(event) =>
              setNewAddress({ ...newAddress, zip: event.target.value })
            }
          />
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="ghost"
              onClick={() => setIsAddressModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={addAddress}>
              Add Address
            </Button>
          </div>
        </div>
      </Modal>

      <Card>
        <SectionHeader
          title="Divisions"
          description="Enable divisions and customize their configuration."
          action={
            <Button
              data-testid="settings-general-add-division-button"
              variant="outline"
              onClick={addDivision}
            >
              Add Division
            </Button>
          }
        />
        <div className="overflow-x-auto border border-border rounded-md">
          <table className="table-sticky-last-action-col min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider w-1/4"
                >
                  Division Name
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider w-1/4"
                >
                  Address
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider w-1/4"
                >
                  Gender / Type
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider w-32"
                >
                  Capacity
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider w-20"
                >
                  Enabled
                </th>
                <th scope="col" className="relative px-4 py-3 w-10">
                  <span className="sr-only">Delete</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border">
              {divisions.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-irongrey">
                    <input
                      className="border-b border-transparent hover:border-border focus:border-primary focus:outline-none bg-transparent"
                      value={row.name}
                      onChange={(event) =>
                        updateDivision(row.id, { name: event.target.value })
                      }
                      onBlur={commitDivisions}
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-text-secondary">
                    <CustomSelect
                      testId={`settings-general-division-address-${row.id}`}
                      options={addressOptions}
                      value={row.address}
                      onChange={(value) => {
                        const next = divisions.map((division) =>
                          division.id === row.id
                            ? { ...division, address: value }
                            : division,
                        );
                        setDivisions(next);
                        void persistDivisions(next);
                      }}
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-text-secondary">
                    <CustomSelect
                      testId={`settings-general-division-gender-${row.id}`}
                      options={[
                        { label: "Boys", value: "Boys" },
                        { label: "Girls", value: "Girls" },
                        { label: "Preschool", value: "Preschool" },
                      ]}
                      value={row.gender}
                      onChange={(value) => {
                        const next = divisions.map((division) =>
                          division.id === row.id
                            ? {
                                ...division,
                                gender: value as DivisionRow["gender"],
                              }
                            : division,
                        );
                        setDivisions(next);
                        void persistDivisions(next);
                      }}
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-text-secondary">
                    <input
                      className="block w-24 rounded-md border-border shadow-sm focus:border-primary focus:ring-primary sm:text-sm h-9 px-2 text-center border"
                      type="number"
                      value={String(row.cap)}
                      onChange={(event) =>
                        updateDivision(row.id, {
                          cap: toFiniteNumber(event.target.value, 0),
                        })
                      }
                      onBlur={commitDivisions}
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={row.enabled}
                      aria-label={`${row.enabled ? "Disable" : "Enable"} ${row.name} division`}
                      disabled={row.enabled && !canDisableDivision(row.id)}
                      title={
                        row.enabled && !canDisableDivision(row.id)
                          ? "At least one division must remain enabled"
                          : undefined
                      }
                      onClick={() => {
                        const next = divisions.map((d) =>
                          d.id === row.id ? { ...d, enabled: !d.enabled } : d,
                        );
                        setDivisions(next);
                        void persistDivisions(next);
                      }}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                        row.enabled ? "bg-primary" : "bg-border"
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${row.enabled ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        const next = divisions.filter(
                          (division) => division.id !== row.id,
                        );
                        setDivisions(next);
                        void persistDivisions(next);
                      }}
                      className="text-text-tertiary hover:text-strawberry transition-colors"
                    >
                      <Icon name="Trash2" className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <SectionHeader
          title="Parent Resources"
          description="Upload handbooks and calendars for the portal."
        />
        <div className="space-y-4">
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="flex items-center justify-between border rounded-lg p-4 border-border bg-white"
            >
              <div className="flex items-center">
                <div
                  className={`h-10 w-10 rounded-lg ${resource.bg} flex items-center justify-center ${resource.color} mr-4`}
                >
                  <Icon name={resource.icon} className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-irongrey">
                    {resource.name}
                  </p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {resource.meta}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.docx"
                  className="sr-only"
                  aria-hidden="true"
                  tabIndex={-1}
                  ref={(el) => {
                    if (el) resourceReplaceRefs.current.set(resource.id, el);
                    else resourceReplaceRefs.current.delete(resource.id);
                  }}
                  onChange={(event) => replaceResource(resource.id, event)}
                />
                <button
                  onClick={() =>
                    resourceReplaceRefs.current.get(resource.id)?.click()
                  }
                  className="text-xs font-medium text-text-secondary hover:text-irongrey transition-colors"
                >
                  Replace
                </button>
                <button
                  onClick={() => deleteResource(resource.id)}
                  className="text-text-tertiary hover:text-strawberry transition-colors"
                >
                  <Icon name="Trash2" className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {resourceGuardError && (
            <p
              className="flex items-center gap-1.5 text-xs text-strawberry"
              role="alert"
            >
              <Icon name="AlertCircle" className="w-3.5 h-3.5 shrink-0" />
              {resourceGuardError}
            </p>
          )}

          {resources.length < MAX_RESOURCES ? (
            <DropzoneField
              accept=".pdf,.png,.jpg,.jpeg,.docx"
              maxSizeBytes={RESOURCE_MAX_BYTES}
              onFile={handleAddResourceFile}
              label="Drop a file here or click to add a resource"
              disabled={resources.length >= MAX_RESOURCES}
            />
          ) : (
            <p className="text-xs text-text-secondary text-center py-2">
              Maximum {MAX_RESOURCES} resources reached. Remove one to add
              another.
            </p>
          )}
        </div>
      </Card>

      {/* ─── Module Enablement ──────────────────────────────────────────────── */}
      {isSuperAdmin ? (
        <motion.div {...moduleMotionProps}>
          <Card data-testid="module-enablement-root">
            <SectionHeader
              title="Module Enablement"
              description="Control which features and routes are available to staff and parents. Changes take effect immediately."
            />

            {/* Always-on notice */}
            <div
              data-testid="module-always-on-routes"
              className="rounded-lg bg-info-background border border-primary/20 px-4 py-3 text-xs text-irongrey mb-4"
            >
              <strong>Always-on routes:</strong> Core settings, authentication,
              and shared shell routes are always enabled and cannot be disabled
              from this panel.
            </div>

            {isLoadingModules ? (
              <p className="text-sm text-text-secondary">
                Loading module governance…
              </p>
            ) : (
              <div className="space-y-8">
                <section aria-labelledby="module-enablement-school-heading">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h4
                        id="module-enablement-school-heading"
                        className="text-sm font-semibold text-irongrey"
                      >
                        School modules
                      </h4>
                      <p className="text-xs text-text-secondary">
                        Hidden and denied when disabled. Route-group toggles
                        refine access inside enabled modules.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {visibleSchoolModules.map((module) => {
                      const moduleStateKey = buildScopedModuleStateKey(
                        "school",
                        module.moduleId,
                      );
                      const isExpanded = expandedModules.has(moduleStateKey);
                      const isPendingDisable =
                        pendingDisableId === moduleStateKey;
                      return (
                        <div
                          key={module.moduleId}
                          data-testid={`module-enablement-card-school-${module.moduleId}`}
                          className={`rounded-lg border border-border bg-white p-4 transition-opacity ${
                            !module.enabled && !module.alwaysOn
                              ? "opacity-60"
                              : ""
                          }`}
                        >
                          {/* Module header row */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-irongrey">
                                  {module.label}
                                </span>
                                {module.alwaysOn ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-success-background text-jungle shrink-0">
                                    Always on
                                  </span>
                                ) : null}
                              </div>
                              <p className="mt-1 text-xs text-text-secondary">
                                {module.summary}
                              </p>
                            </div>

                            {!module.alwaysOn ? (
                              <button
                                type="button"
                                role="switch"
                                aria-checked={module.enabled}
                                aria-label={buildModuleToggleAriaLabel(
                                  "school",
                                  module,
                                )}
                                disabled={isSavingModules || isPendingDisable}
                                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                  module.enabled ? "bg-primary" : "bg-border"
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                onClick={() =>
                                  handleSchoolModuleToggleIntent(
                                    module.moduleId,
                                    module.enabled,
                                  )
                                }
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    module.enabled
                                      ? "translate-x-4"
                                      : "translate-x-0"
                                  }`}
                                />
                              </button>
                            ) : null}
                          </div>

                          {/* Gap 2 — Hide-and-deny callout */}
                          {!module.enabled && !module.alwaysOn ? (
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-strawberry/80">
                              <Icon
                                name="EyeOff"
                                className="w-3.5 h-3.5 shrink-0"
                              />
                              {buildDisabledModuleMessage(module)}
                            </div>
                          ) : null}

                          {/* Gap 3 — Disable confirmation row */}
                          {isPendingDisable ? (
                            <div
                              data-testid={`module-enablement-confirm-disable-${module.moduleId}`}
                              className="mt-3 rounded-md bg-error-background border border-strawberry/20 px-4 py-3 flex items-start gap-3"
                            >
                              <Icon
                                name="AlertTriangle"
                                className="w-4 h-4 text-strawberry mt-0.5 shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-irongrey">
                                  Disable {module.label}?
                                </p>
                                <p className="text-xs text-text-secondary mt-0.5">
                                  {buildDisableModuleConfirmation(module)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  variant="ghost"
                                  onClick={() => setPendingDisableId(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="primary"
                                  className="!bg-strawberry !border-strawberry"
                                  onClick={() =>
                                    void confirmDisableModule(module.moduleId)
                                  }
                                >
                                  Disable
                                </Button>
                              </div>
                            </div>
                          ) : null}

                          {/* Gap 1 — Progressive disclosure for route groups */}
                          {module.routeGroups.length > 0 ? (
                            <>
                              <button
                                type="button"
                                data-testid={`module-enablement-expand-routes-${module.moduleId}`}
                                onClick={() =>
                                  toggleModuleExpanded(
                                    "school",
                                    module.moduleId,
                                  )
                                }
                                className="mt-3 text-xs text-primary hover:text-jungle transition-colors focus:outline-none focus:underline"
                              >
                                {isExpanded
                                  ? "Hide route groups"
                                  : `Show route groups (${module.routeGroups.length})`}
                              </button>

                              {isExpanded ? (
                                <div className="mt-2 ml-1 space-y-2 border-l-2 border-border pl-4">
                                  {module.routeGroups.map((routeGroup) => (
                                    <div
                                      key={routeGroup.routeGroupId}
                                      data-testid={`module-route-group-toggle-${routeGroup.routeGroupId}`}
                                      className="flex items-center justify-between gap-3 py-0.5"
                                    >
                                      <span className="text-xs text-text-secondary">
                                        {routeGroup.label}
                                      </span>
                                      <button
                                        type="button"
                                        role="switch"
                                        aria-checked={routeGroup.enabled}
                                        aria-label={buildRouteGroupToggleAriaLabel(
                                          module,
                                          routeGroup.label,
                                        )}
                                        disabled={
                                          isSavingModules ||
                                          module.alwaysOn ||
                                          !module.enabled
                                        }
                                        className={`relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                          routeGroup.enabled
                                            ? "bg-primary"
                                            : "bg-border"
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                        onClick={() =>
                                          void toggleRouteGroup(
                                            "school",
                                            module.moduleId,
                                            routeGroup.routeGroupId,
                                            !routeGroup.enabled,
                                          )
                                        }
                                      >
                                        <span
                                          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            routeGroup.enabled
                                              ? "translate-x-3"
                                              : "translate-x-0"
                                          }`}
                                        />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section aria-labelledby="module-enablement-parent-heading">
                  <div className="mb-3">
                    <h4
                      id="module-enablement-parent-heading"
                      className="text-sm font-semibold text-irongrey"
                    >
                      Parent portal modules
                    </h4>
                    <p className="text-xs text-text-secondary">
                      Parent modules remain independently toggleable and must
                      declare explicit dependency mode. Temp preview modules
                      hide from the shell only; direct preview routes remain
                      mounted for review.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {visibleParentModules.map((module) => {
                      const parentEnabled = module.dependsOnSchoolModuleId
                        ? isSchoolModuleEnabled(
                            module.dependsOnSchoolModuleId,
                            moduleEnablementState.schoolModules,
                          )
                        : true;
                      const moduleStateKey = buildScopedModuleStateKey(
                        "parent",
                        module.moduleId,
                      );
                      const isExpanded = expandedModules.has(moduleStateKey);
                      return (
                        <div
                          key={module.moduleId}
                          data-testid={`module-enablement-card-parent-${module.moduleId}`}
                          className={`rounded-lg border border-border bg-white p-4 transition-opacity ${
                            !module.enabled && !module.alwaysOn
                              ? "opacity-60"
                              : ""
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-irongrey">
                                  {module.label}
                                </span>
                                {module.dependencyMode ? (
                                  <span
                                    data-testid={`module-parent-dependency-${module.moduleId}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted border border-border text-xs text-text-secondary"
                                  >
                                    <Icon name="Link" className="w-3 h-3" />
                                    {dependencyModeLabel[module.dependencyMode]}
                                    {module.dependsOnSchoolModuleId
                                      ? `: ${schoolModuleLabelById.get(module.dependsOnSchoolModuleId) ?? module.dependsOnSchoolModuleId}`
                                      : ""}
                                  </span>
                                ) : null}
                              </div>
                              <p className="mt-1 text-xs text-text-secondary">
                                {module.summary}
                              </p>
                            </div>

                            <button
                              type="button"
                              role="switch"
                              aria-checked={module.enabled}
                              aria-label={buildModuleToggleAriaLabel(
                                "parent",
                                module,
                              )}
                              disabled={isSavingModules || !parentEnabled}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                module.enabled ? "bg-primary" : "bg-border"
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                              onClick={() =>
                                void toggleModule(
                                  "parent",
                                  module.moduleId,
                                  !module.enabled,
                                )
                              }
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  module.enabled
                                    ? "translate-x-4"
                                    : "translate-x-0"
                                }`}
                              />
                            </button>
                          </div>

                          {!module.enabled && !module.alwaysOn ? (
                            <div className="mt-2 flex items-center gap-1.5 text-xs text-strawberry/80">
                              <Icon
                                name="EyeOff"
                                className="w-3.5 h-3.5 shrink-0"
                              />
                              {buildDisabledModuleMessage(module)}
                            </div>
                          ) : null}

                          {!parentEnabled && module.dependsOnSchoolModuleId ? (
                            <p className="mt-3 text-xs text-strawberry">
                              This parent module stays unavailable until the
                              linked school module is enabled.
                            </p>
                          ) : null}

                          {module.routeGroups.length > 0 ? (
                            <>
                              <button
                                type="button"
                                data-testid={`module-enablement-expand-routes-parent-${module.moduleId}`}
                                onClick={() =>
                                  toggleModuleExpanded(
                                    "parent",
                                    module.moduleId,
                                  )
                                }
                                className="mt-3 text-xs text-primary hover:text-jungle transition-colors focus:outline-none focus:underline"
                              >
                                {isExpanded
                                  ? "Hide route groups"
                                  : `Show route groups (${module.routeGroups.length})`}
                              </button>

                              {isExpanded ? (
                                <div className="mt-2 ml-1 space-y-2 border-l-2 border-border pl-4">
                                  {module.routeGroups.map((routeGroup) => (
                                    <div
                                      key={routeGroup.routeGroupId}
                                      data-testid={`module-route-group-toggle-${routeGroup.routeGroupId}`}
                                      className="flex items-center justify-between gap-3 py-0.5"
                                    >
                                      <span className="text-xs text-text-secondary">
                                        {routeGroup.label}
                                      </span>
                                      <button
                                        type="button"
                                        role="switch"
                                        aria-checked={routeGroup.enabled}
                                        aria-label={buildRouteGroupToggleAriaLabel(
                                          module,
                                          routeGroup.label,
                                        )}
                                        disabled={
                                          isSavingModules ||
                                          module.alwaysOn ||
                                          !module.enabled ||
                                          !parentEnabled
                                        }
                                        className={`relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                          routeGroup.enabled
                                            ? "bg-primary"
                                            : "bg-border"
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                        onClick={() =>
                                          void toggleRouteGroup(
                                            "parent",
                                            module.moduleId,
                                            routeGroup.routeGroupId,
                                            !routeGroup.enabled,
                                          )
                                        }
                                      >
                                        <span
                                          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            routeGroup.enabled
                                              ? "translate-x-3"
                                              : "translate-x-0"
                                          }`}
                                        />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <div className="rounded-lg border border-border bg-muted px-4 py-3 text-xs text-text-secondary">
                  Disabled modules are removed from sidebar navigation and
                  direct route access is denied in the shell. The Temp toggle is
                  the explicit exception: it controls preview sidebar exposure
                  only and stays off by default.
                </div>

                {/* Gap 4 — Audit summary stub */}
                <div
                  data-testid="module-enablement-audit-summary"
                  className="mt-6 rounded-lg border border-border bg-muted/40 px-4 py-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Clock" className="w-4 h-4 text-text-tertiary" />
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                      Recent changes
                    </span>
                  </div>
                  {isLoadingModuleAudit ? (
                    <p className="text-xs text-text-tertiary italic">
                      Loading governance activity…
                    </p>
                  ) : (moduleAuditData?.events?.length ?? 0) > 0 ? (
                    <div className="space-y-2">
                      {moduleAuditData?.events?.map((event) => (
                        <div
                          key={event.eventId}
                          className="rounded-md border border-border bg-white/80 px-3 py-2"
                        >
                          <p className="text-xs font-medium text-irongrey">
                            {event.moduleLabel}
                            {event.routeGroupLabel
                              ? ` · ${event.routeGroupLabel}`
                              : ""}
                          </p>
                          <p className="mt-0.5 text-xs text-text-secondary">
                            {event.actorEmail} ·{" "}
                            {event.action.replace(/_/g, " ")} ·{" "}
                            {event.occurredAt}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-text-tertiary italic">
                      No governance changes have been recorded yet.
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      ) : null}

      <motion.div {...moduleMotionProps}>
        <Card data-testid="settings-general-parnes-hayom-pricing-card">
          <SectionHeader
            title="Parnes Hayom Sponsorship Pricing"
            description="These dollar amounts drive the parent sponsorship options shown on /parent/portal/parnes-hayom. The one-division option uses the enabled divisions configured above."
          />

          <section className="mb-5 flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-irongrey">
                Show in Parent Portal dashboard
              </p>
              <p className="text-xs text-text-secondary">
                Turn off to hide the Parnes HaYom panel for all parent users.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isParnesHayomVisibleToParents}
              aria-label="Show in Parent Portal dashboard"
              data-testid="settings-general-parnes-hayom-visibility-toggle"
              onClick={handleParnesHayomVisibilityToggle}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${
                isParnesHayomVisibleToParents
                  ? "border-transparent bg-primary"
                  : "border-border bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  isParnesHayomVisibleToParents
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
          </section>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Input
              label="One Class"
              subLabel="Shown as $180 for one class by default."
              type="number"
              min="0"
              step="1"
              value={parnesHayomPricing.oneClassAmount}
              onChange={(event) =>
                updateParnesHayomPricingField(
                  "oneClassAmount",
                  event.currentTarget.value,
                )
              }
              onBlur={handleParnesHayomPricingBlur}
              data-testid="settings-general-parnes-hayom-one-class-input"
            />
            <Input
              label="One Division"
              subLabel="Shown when a parent chooses the division-sponsored option."
              type="number"
              min="0"
              step="1"
              value={parnesHayomPricing.oneDivisionAmount}
              onChange={(event) =>
                updateParnesHayomPricingField(
                  "oneDivisionAmount",
                  event.currentTarget.value,
                )
              }
              onBlur={handleParnesHayomPricingBlur}
              data-testid="settings-general-parnes-hayom-one-division-input"
            />
            <Input
              label="Schoolwide"
              subLabel="Shown as the schoolwide sponsorship amount."
              type="number"
              min="0"
              step="1"
              value={parnesHayomPricing.schoolwideAmount}
              onChange={(event) =>
                updateParnesHayomPricingField(
                  "schoolwideAmount",
                  event.currentTarget.value,
                )
              }
              onBlur={handleParnesHayomPricingBlur}
              data-testid="settings-general-parnes-hayom-schoolwide-input"
            />
          </div>

          <div className="mt-4 rounded-lg border border-border bg-muted px-4 py-3 text-xs text-text-secondary">
            Parents can choose one class, one division, or schoolwide on both
            the calendar page and checkout. Division choices come directly from
            the enabled school divisions in this settings surface.
          </div>
        </Card>
      </motion.div>

    </div>
  );
}
